import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AlertService } from '../Alert/alert.service';
import { BaseService } from '../base.service';

export interface ApiResponse<T> {
  value?: T;
  valueobjs?: T;
  message?: string;
}

export interface RegistrationUser {
  email: string;
  name: string;
  divisionId?: number;
}

export interface RegistrationDivision {
  id: number;
  name: string;
}

export type UserRegistrationRequestStatusValue = 'Pending' | 'Approved' | 'Declined' | 'Expired';

export interface UserRegistrationRequest {
  id: number;
  userId: number;
  fullName: string;
  email: string;
  managerEmail: string;
  reportingManager: string;
  divisionId: number;
  division: string;
  status: UserRegistrationRequestStatusValue;
  expiresOn: string;
  decisionDate?: string;
  decisionBy?: string;
  delegates: RegistrationUser[];
}

export interface UserRegistrationRequestStatus {
  userExists: boolean;
  userStatusId?: number;
  hasPendingRequest: boolean;
  canSubmitRequest: boolean;
  request?: UserRegistrationRequest;
}

export interface CreateUserRegistrationRequest {
  managerEmail: string;
  divisionId: number;
  delegateEmails: string[];
}

@Injectable({
  providedIn: 'root',
})
export class RegistrationRequestService extends BaseService {
  constructor(
    private http: HttpClient,
    alertService: AlertService
  ) {
    super('registration-request', alertService);
  }

  getStatus(): Observable<ApiResponse<UserRegistrationRequestStatus>> {
    return this.http
      .get<ApiResponse<UserRegistrationRequestStatus>>(`${this.url}/me`)
      .pipe(map((data) => data));
  }

  getDivisions(): Observable<ApiResponse<RegistrationDivision[]>> {
    return this.http
      .get<ApiResponse<RegistrationDivision[]>>(`${this.url}/divisions`)
      .pipe(map((data) => data));
  }

  searchManagers(filter: string): Observable<ApiResponse<RegistrationUser[]>> {
    let params = new HttpParams();
    params = params.set('filter', filter);

    return this.http
      .get<ApiResponse<RegistrationUser[]>>(`${this.url}/managers`, { params: params })
      .pipe(map((data) => data));
  }

  searchDelegates(filter: string): Observable<ApiResponse<RegistrationUser[]>> {
    let params = new HttpParams();
    params = params.set('filter', filter);

    return this.http
      .get<ApiResponse<RegistrationUser[]>>(`${this.url}/delegates`, { params: params })
      .pipe(map((data) => data));
  }

  submit(request: CreateUserRegistrationRequest): Observable<ApiResponse<UserRegistrationRequest>> {
    return this.http
      .post<ApiResponse<UserRegistrationRequest>>(this.url, request)
      .pipe(map((data) => data));
  }

  getRequest(id: number): Observable<ApiResponse<UserRegistrationRequest>> {
    return this.http
      .get<ApiResponse<UserRegistrationRequest>>(`${this.url}/${id}`)
      .pipe(map((data) => data));
  }

  approve(id: number): Observable<ApiResponse<UserRegistrationRequest>> {
    return this.http
      .post<ApiResponse<UserRegistrationRequest>>(`${this.url}/${id}/approve`, null)
      .pipe(map((data) => data));
  }

  decline(id: number): Observable<ApiResponse<UserRegistrationRequest>> {
    return this.http
      .post<ApiResponse<UserRegistrationRequest>>(`${this.url}/${id}/decline`, null)
      .pipe(map((data) => data));
  }
}
